import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CollectiveTypeBadge } from "@/components/CollectiveTypeBadge";
import { StartCollectiveDialog } from "@/components/collectives/StartCollectiveDialog";
import { Chip } from "@/components/collectives/Chip";
import MapLeaflet from "@/components/MapLeaflet";
import { Seo } from "@/components/Seo";
import {
  MapPinned, Users, Crown, Clock, Sprout, ChevronRight, Search, X,
  List as ListIcon, Map as MapIcon, Sparkles, Moon,
} from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

// ────────────────────────────────────────────────────────────────────────────
// Item 5: "tell me when one starts" capture — stores email + outward code ONLY.
// ────────────────────────────────────────────────────────────────────────────
function NotifyInterestForm({ outcode }) {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.collectiveNotifyInterest(email.trim(), outcode);
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save that — check the email address");
    } finally { setBusy(false); }
  };
  if (done) {
    return (
      <p data-testid="notify-interest-done" className="text-sm font-medium text-forest mt-5">
        You're on the list — we'll email you the moment a collective starts near {outcode}.
      </p>
    );
  }
  return (
    <form onSubmit={submit} className="mt-5" data-testid="notify-interest-form">
      <div className="flex gap-2 justify-center flex-wrap">
        <Input
          data-testid="notify-interest-email"
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="h-10 rounded-xl max-w-[240px] bg-white"
        />
        <Button type="submit" disabled={busy} variant="outline" data-testid="notify-interest-submit" className="rounded-full h-10 border-forest/30 text-forest hover:bg-forest/5">
          {busy ? "Saving…" : "Tell me when one starts"}
        </Button>
      </div>
      <p className="text-[11px] text-neutral-500 mt-2">We store your email and the {outcode} district only — nothing else.</p>
    </form>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Main
// ────────────────────────────────────────────────────────────────────────────
export default function Collectives() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();

  // URL-synced discovery state
  const q            = sp.get("q") || "";
  const mode         = sp.get("mode") || "";                  // "", "open", "review"
  const ctypeFilter  = sp.get("ctype") || "";                 // "", "volunteering", "social"
  const size         = sp.get("size") || "";                  // "", "small", "growing", "established"
  const nearMe       = sp.get("near") === "1";
  const newThisMonth = sp.get("new") === "1";
  const matched      = sp.get("matched") === "1";
  const pc           = sp.get("pc") || "";                    // item 5: postcode search
  // Merged search (2026-02): ONE box accepts a postcode OR name/area/keyword.
  // Postcode-shaped input → geo path (pc param, distance sort); anything
  // else → text search path (q param). Regexes mirror backend geo.py.
  const FULL_PC_RE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/;
  const OUTCODE_RE = /^[A-Za-z]{1,2}\d[A-Za-z\d]?$/;
  const [searchDraft, setSearchDraft] = useState(pc || q);
  const [pcLoc, setPcLoc] = useState(null);   // {label, lat, lng, outcode} — resolved, never persisted
  const [pcErr, setPcErr] = useState("");

  const [items, setItems]   = useState([]);
  const [mine, setMine]     = useState(null);
  const [loading, setLoad]  = useState(true);
  const [view, setView]     = useState("list");               // mobile toggle: "list" | "map"
  const [inviteInfo, setInviteInfo] = useState(null);
  const [joiningInvite, setJoiningInvite] = useState(false);

  const hasUserGeo = user?.lat != null && user?.lng != null;
  // Proxy for "user has an embedding on the backend" — see auth.py: we embed
  // whenever bio/skills/values are non-empty and never expose the vector on the wire.
  const hasProfileText = Boolean(user && (
    (user.bio && user.bio.trim()) ||
    (user.skills && user.skills.length > 0) ||
    (user.values && user.values.length > 0)
  ));

  // Sync the merged draft when pc/q change externally (e.g. clear-filters click)
  useEffect(() => { setSearchDraft(pc || q); }, [pc, q]);

  // Item 5: resolve the pc URL param (full or outward postcode) to coords.
  // The postcode is used for the lookup only — nothing is persisted.
  useEffect(() => {
    if (!pc) { setPcLoc(null); setPcErr(""); return; }
    let alive = true;
    api.geoPostcode(pc)
      .then((r) => { if (alive) { setPcLoc(r); setPcErr(""); } })
      .catch((e) => { if (alive) { setPcLoc(null); setPcErr(e?.response?.data?.detail || "Couldn't find that postcode — try the district part (e.g. SW9)"); } });
    return () => { alive = false; };
  }, [pc]);

  // Merged search submit: route the input to the right path.
  const submitSearch = (raw) => {
    const v = (raw ?? searchDraft).trim();
    const next = new URLSearchParams(sp);
    next.delete("pc"); next.delete("q");
    if (v) {
      const compact = v.replace(/\s+/g, "");
      if (FULL_PC_RE.test(v) || OUTCODE_RE.test(compact)) next.set("pc", v);
      else next.set("q", v);
    }
    setSp(next, { replace: true });
  };

  const setParam = (key, val) => {
    const next = new URLSearchParams(sp);
    if (val == null || val === "" || val === false) next.delete(key);
    else next.set(key, val === true ? "1" : String(val));
    setSp(next, { replace: true });
  };

  const clearAll = () => setSp(new URLSearchParams(), { replace: true });

  // Build API params from URL state
  const apiParams = useMemo(() => {
    const p = {};
    if (q) p.q = q;
    if (mode) p.mode = mode;
    if (ctypeFilter) p.ctype = ctypeFilter;
    if (size) p.size = size;
    if (newThisMonth) {
      const start = dayjs().startOf("month").toISOString();
      p.since = start;
    }
    if (pcLoc) {
      // Postcode search takes precedence: distance-sorted within 25 miles.
      p.near_lat = pcLoc.lat;
      p.near_lng = pcLoc.lng;
      p.near_miles = 25;
    } else if (nearMe && hasUserGeo) {
      p.near_lat = user.lat;
      p.near_lng = user.lng;
      p.near_miles = 25;
    } else if (hasUserGeo) {
      // Distance annotation without filtering
      p.lat = user.lat;
      p.lng = user.lng;
    }
    if (matched && hasProfileText) {
      p.matched = 1;
    }
    return p;
  }, [q, mode, ctypeFilter, size, nearMe, newThisMonth, matched, hasProfileText, hasUserGeo, user?.lat, user?.lng, pcLoc]);

  const load = () => {
    setLoad(true);
    api.collectives(apiParams).then(setItems).catch(() => setItems([])).finally(() => setLoad(false));
    if (user) api.myCollectives().then(setMine).catch(() => {});
  };
  // `load` is re-created every render; the effect intentionally keys on its
  // actual inputs (filter params + auth identity) — depping `load` itself
  // would re-fetch on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [apiParams, user?.user_id]);

  // Invite handling (unchanged)
  useEffect(() => {
    let code = null;
    try {
      const url = new URLSearchParams(window.location.search);
      code = url.get("invite") || sessionStorage.getItem("collective_invite") || null;
      if (code) code = code.toUpperCase().trim();
    } catch { /* ignore */ }
    if (!code || !/^[A-Z0-9]{6,12}$/.test(code)) return;
    api.collectiveByInvite(code)
      .then((c) => setInviteInfo({ ...c, invite_code: code }))
      .catch(() => setInviteInfo(null));
  }, []);

  const acceptInvite = async () => {
    if (!inviteInfo) return;
    if (!user) return nav("/login");
    setJoiningInvite(true);
    try {
      const res = await api.joinByInvite(inviteInfo.invite_code);
      try { sessionStorage.removeItem("collective_invite"); } catch { /* ignore */ }
      if (res?.status === "pending") toast(res.message || "Request sent — a leader will review it.");
      else toast.success(`Welcome to ${inviteInfo.name}!`);
      nav(`/collectives/${inviteInfo.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join via invite");
    } finally { setJoiningInvite(false); }
  };

  const myIds = new Set((mine?.collectives || []).map(c => c.id));
  const pendingApp  = (mine?.applications || []).find(a => a.status === "pending");
  const rejectedApp = (mine?.applications || []).find(a => a.status === "rejected");

  const filtersActive = Boolean(q || mode || size || nearMe || newThisMonth || matched || ctypeFilter || pc);

  // Map props
  const markers = items
    .filter(c => c.lat != null && c.lng != null)
    .map(c => ({
      lat: c.lat, lng: c.lng,
      title: c.name,
      subtitle: `${c.area_label} · ${c.member_count} member${c.member_count !== 1 ? "s" : ""}`,
      link: `/collectives/${c.id}`,
    }));
  const circles = items
    .filter(c => c.lat != null && c.lng != null)
    .map(c => {
      const active = (c.events_this_month || 0) > 0;
      if (active) {
        return { lat: c.lat, lng: c.lng, radius_miles: c.radius_miles || 5,
                 color: "#f59e0b", fillOpacity: 0.20 };
      }
      const mine = myIds.has(c.id);
      return { lat: c.lat, lng: c.lng, radius_miles: c.radius_miles || 5,
               color: mine ? "#D36135" : "#1A4331",
               fillOpacity: mine ? 0.12 : 0.06 };
    });

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="collectives-page">
      <Seo title="Collectives" description="Discover neighbourhood collectives — filter by area, size, and activity, then join or start your own." />

      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Grassroots</div>
          <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Collectives</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Local groups run by Community Leaders. Search, browse the map, and join the one closest to you.
          </p>
        </div>
        <StartCollectiveDialog onCreated={load} autoOpen={sp.get("start") === "1"} />
      </div>

      {inviteInfo && (
        <div data-testid="invite-banner" className="mt-6 bg-terracotta/10 border border-terracotta/30 rounded-xl px-5 py-4 flex items-start gap-3">
          <MapPinned className="w-5 h-5 text-terracotta shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-sm text-forest">
              <span className="font-medium">{inviteInfo.name}</span> invited you to join their collective in {inviteInfo.area_label}
              {inviteInfo.membership_mode === "review" ? " (leader-reviewed)" : ""}.
            </div>
            <p className="text-xs text-neutral-600 mt-1">
              {user ? `You still need to be within ${inviteInfo.radius_miles} miles of the area to join.` : "Sign in to accept this invite."}
            </p>
          </div>
          {user ? (
            <Button data-testid="invite-join-btn" onClick={acceptInvite} disabled={joiningInvite} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-xs">
              {joiningInvite ? "Joining…" : (inviteInfo.membership_mode === "review" ? "Request to join" : "Join collective")}
            </Button>
          ) : (
            <Button data-testid="invite-signin-btn" onClick={() => nav("/login")} className="rounded-full bg-forest hover:bg-forest-hover h-9 px-4 text-xs">Sign in</Button>
          )}
        </div>
      )}

      {pendingApp && (
        <div data-testid="collective-pending-banner" className="mt-6 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 text-sm text-amber-800">
          <Clock className="w-4 h-4 shrink-0" /> Your application to lead "{pendingApp.name}" is being reviewed — we'll notify you.
        </div>
      )}
      {rejectedApp && !pendingApp && (
        <div data-testid="collective-rejected-banner" className="mt-6 bg-red-50 border border-red-200 rounded-xl px-5 py-3 text-sm text-red-700">
          Your application for "{rejectedApp.name}" wasn't approved{rejectedApp.rejection_reason ? ` — ${rejectedApp.rejection_reason}` : ""}. You're welcome to apply again.
        </div>
      )}

      {/* Mobile view toggle */}
      <div className="mt-8 lg:hidden inline-flex rounded-full border border-neutral-300 p-1 bg-white">
        <button
          data-testid="collectives-view-toggle-list"
          onClick={() => setView("list")}
          className={"px-4 h-9 rounded-full text-xs font-medium inline-flex items-center gap-1.5 " + (view === "list" ? "bg-forest text-white" : "text-forest")}
        ><ListIcon className="w-3.5 h-3.5" /> List</button>
        <button
          data-testid="collectives-view-toggle-map"
          onClick={() => setView("map")}
          className={"px-4 h-9 rounded-full text-xs font-medium inline-flex items-center gap-1.5 " + (view === "map" ? "bg-forest text-white" : "text-forest")}
        ><MapIcon className="w-3.5 h-3.5" /> Map</button>
      </div>

      {/* Two-column: left = search+filters+list, right = sticky map */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* LEFT column */}
        <div className={"lg:col-span-7 " + (view === "map" ? "hidden lg:block" : "")}>
          {/* Merged search: postcode OR name/area/keyword (2026-02) */}
          <div data-testid="postcode-search-box" className="bg-forest/5 border border-forest/10 rounded-2xl p-4 mb-4">
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Is my street on here?</div>
            <form
              onSubmit={(e) => { e.preventDefault(); submitSearch(); }}
              className="mt-2 flex gap-2"
            >
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-neutral-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="postcode-input"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Postcode, street, or collective name"
                  className="h-11 pl-10 rounded-xl bg-white"
                />
              </div>
              <Button type="submit" data-testid="postcode-search-btn" className="rounded-full bg-forest hover:bg-forest-hover h-11 px-5 shrink-0">
                Search
              </Button>
              {(pc || q) && (
                <Button type="button" variant="ghost" data-testid="postcode-clear" onClick={() => submitSearch("")} className="rounded-full h-11 px-3 text-neutral-500 shrink-0">
                  <X className="w-4 h-4" />
                </Button>
              )}
            </form>
            {pcErr && <p data-testid="postcode-error" className="text-xs text-red-600 mt-2">{pcErr}</p>}
            <p className="text-[11px] text-neutral-500 mt-2">Postcodes are used for this lookup only — we never store yours.</p>
          </div>

          {/* Filter chips */}
          <div className="mt-4 flex flex-wrap gap-2">
            <Chip testId="filter-chip-type-all" active={!ctypeFilter}
              onClick={() => setParam("ctype", "")}
            >All</Chip>
            <Chip testId="filter-chip-type-volunteering" active={ctypeFilter === "volunteering"}
              onClick={() => setParam("ctype", ctypeFilter === "volunteering" ? "" : "volunteering")}
              title="Collectives organising volunteer work (includes mixed ones)"
            >Volunteering</Chip>
            <Chip testId="filter-chip-type-social" active={ctypeFilter === "social"}
              onClick={() => setParam("ctype", ctypeFilter === "social" ? "" : "social")}
              title="Social meetup collectives (includes mixed ones)"
            >Social</Chip>
            <span className="w-px h-6 bg-neutral-200 self-center mx-1" />
            <Chip
              testId="filter-chip-matched"
              active={matched}
              disabled={!hasProfileText}
              onClick={() => setParam("matched", !matched)}
              title={hasProfileText ? "Collectives that match your bio, values and skills" : "Add a bio and some skills to your profile to enable"}
            >
              <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3" /> Matched for me</span>
            </Chip>
            <Chip
              testId="filter-chip-near-me"
              active={nearMe}
              disabled={!hasUserGeo}
              onClick={() => setParam("near", !nearMe)}
              title={hasUserGeo ? "Show collectives within 25 miles" : "Set your location in profile to enable"}
            >Near me</Chip>
            <Chip testId="filter-chip-new-this-month" active={newThisMonth}
              onClick={() => setParam("new", !newThisMonth)}
            >New this month</Chip>
            <Chip testId="filter-chip-open" active={mode === "open"}
              onClick={() => setParam("mode", mode === "open" ? "" : "open")}
            >Open to join</Chip>
            <Chip testId="filter-chip-review" active={mode === "review"}
              onClick={() => setParam("mode", mode === "review" ? "" : "review")}
            >Leader review</Chip>
            <span className="w-px h-6 bg-neutral-200 self-center mx-1" />
            <Chip testId="filter-chip-size-small" active={size === "small"}
              onClick={() => setParam("size", size === "small" ? "" : "small")}
            >Small · &lt;10</Chip>
            <Chip testId="filter-chip-size-growing" active={size === "growing"}
              onClick={() => setParam("size", size === "growing" ? "" : "growing")}
            >Growing · 10–49</Chip>
            <Chip testId="filter-chip-size-established" active={size === "established"}
              onClick={() => setParam("size", size === "established" ? "" : "established")}
            >Established · 50+</Chip>
            {filtersActive && (
              <button
                data-testid="filter-chip-clear-all"
                onClick={clearAll}
                className="text-xs text-neutral-500 hover:text-forest underline underline-offset-2 ml-1 self-center"
              >Clear all</button>
            )}
          </div>

          {/* Result count */}
          <div className="mt-4 text-xs uppercase tracking-[0.18em] text-forest/60 font-bold" data-testid="collectives-count">
            {loading
              ? "Loading…"
              : pcLoc
                ? `${items.length} collective${items.length !== 1 ? "s" : ""} near ${pcLoc.outcode}`
                : q
                  ? `${items.length} collective${items.length !== 1 ? "s" : ""} matching "${q}"`
                  : `${items.length} collective${items.length !== 1 ? "s" : ""}`}
          </div>

          {/* List / empty state */}
          {!loading && items.length === 0 && pcLoc ? (
            <div data-testid="postcode-empty-state" className="mt-6 text-center py-14 bg-white border border-dashed border-terracotta/40 rounded-2xl px-6">
              <Sprout className="w-10 h-10 text-terracotta/50 mx-auto" />
              <div className="font-display text-2xl text-forest mt-3">
                No collective near {pcLoc.outcode} yet — start the first one
              </div>
              <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">
                Be the neighbour who gets {pcLoc.outcode} organised — we'll walk you through it.
              </p>
              <div className="mt-6">
                <StartCollectiveDialog
                  onCreated={load}
                  triggerVariant="empty"
                  initialArea={{ label: pcLoc.label, lat: pcLoc.lat, lng: pcLoc.lng }}
                />
              </div>
              <div className="mt-6 pt-5 border-t border-neutral-100">
                <div className="text-xs uppercase tracking-[0.18em] text-forest/60 font-bold">Not ready to lead?</div>
                <NotifyInterestForm outcode={pcLoc.outcode} />
              </div>
            </div>
          ) : !loading && items.length === 0 ? (
            <div data-testid="collectives-empty-state" className="mt-6 text-center py-14 bg-white border border-dashed border-neutral-300 rounded-2xl">
              <Sprout className="w-10 h-10 text-forest/30 mx-auto" />
              <div className="font-display text-xl text-forest mt-3">No collectives match your filters.</div>
              <p className="text-sm text-neutral-500 mt-1 max-w-md mx-auto">
                Not seeing one near you? Start your own — you'll lead your local community.
              </p>
              <div className="mt-6 inline-flex items-center gap-3">
                <StartCollectiveDialog onCreated={load} triggerVariant="empty" />
                {filtersActive && (
                  <button
                    data-testid="collectives-empty-clear"
                    onClick={clearAll}
                    className="text-sm text-forest underline underline-offset-2 hover:text-forest-hover"
                  >Clear filters</button>
                )}
              </div>
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {items.map((c, idx) => {
                const isMember = myIds.has(c.id);
                const ago = c.created_at ? dayjs(c.created_at).fromNow() : null;
                return (
                  <Link
                    key={c.id}
                    to={`/collectives/${c.id}`}
                    data-testid={`collective-card-${c.id}`}
                    className="group block bg-white border border-neutral-200 rounded-2xl p-5 hover:border-forest/40 hover:shadow-sm transition-all animate-fade-up"
                    style={{ animationDelay: `${Math.min(idx, 6) * 40}ms` }}
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-11 h-11 rounded-xl bg-forest/10 flex items-center justify-center shrink-0">
                        <MapPinned className="w-5 h-5 text-forest" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-display text-lg text-forest truncate">{c.name}</div>
                          <CollectiveTypeBadge type={c.collective_type} testId={`collective-type-badge-${c.id}`} />
                          {isMember && <Badge className="rounded-full bg-forest text-white border-none text-[10px]">Member</Badge>}
                          {c.membership_mode === "review" && (
                            <Badge variant="outline" className="rounded-full text-[10px] border-neutral-300 text-neutral-600">Leader review</Badge>
                          )}
                        </div>
                        <div className="text-xs text-neutral-500 mt-0.5">
                          {c.area_label} · {c.radius_miles} mi radius
                          {ago && <> · <span title={c.created_at}>Created {ago}</span></>}
                        </div>
                        <p className="text-sm text-neutral-600 mt-2 line-clamp-2">{c.description}</p>
                        <div className="flex items-center gap-4 mt-3 text-xs">
                          <span className="inline-flex items-center gap-1.5 text-neutral-600">
                            <Users className="w-3.5 h-3.5 text-forest/60" /> {c.member_count} member{c.member_count !== 1 ? "s" : ""}
                          </span>
                          {c.events_this_month > 0 ? (
                            <span
                              data-testid={`card-events-count-${c.id}`}
                              className="inline-flex items-center gap-1.5 text-forest font-medium"
                            >
                              <Sparkles className="w-3.5 h-3.5 text-terracotta" /> {c.events_this_month} event{c.events_this_month !== 1 ? "s" : ""} this month
                            </span>
                          ) : c.quiet ? (
                            <span
                              data-testid={`quiet-chip-${c.id}`}
                              className="inline-flex items-center gap-1.5 text-neutral-400"
                            >
                              <Moon className="w-3.5 h-3.5" /> Quiet lately
                            </span>
                          ) : (
                            <span
                              data-testid={`card-events-count-${c.id}`}
                              className="inline-flex items-center gap-1.5 text-neutral-400"
                            >
                              <Sprout className="w-3.5 h-3.5" /> Getting started
                            </span>
                          )}
                          {c.distance_miles != null && (
                            <span className="text-neutral-500 ml-auto">{c.distance_miles} mi away</span>
                          )}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-neutral-400 group-hover:translate-x-0.5 transition-transform mt-1" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* RIGHT column — sticky map */}
        <div className={"lg:col-span-5 " + (view === "list" ? "hidden lg:block" : "")}>
          <div className="lg:sticky lg:top-24">
            <MapLeaflet
              testId="collectives-map"
              markers={markers}
              circles={circles}
              userLocation={pcLoc ? { lat: pcLoc.lat, lng: pcLoc.lng } : (hasUserGeo ? { lat: user.lat, lng: user.lng } : null)}
              height="calc(100vh - 220px)"
              fitBounds
              center={pcLoc ? [pcLoc.lat, pcLoc.lng] : (hasUserGeo ? [user.lat, user.lng] : [54.5, -2.5])}
              zoom={pcLoc ? 11 : (hasUserGeo ? 10 : 6)}
            />
            <div
              data-testid="map-legend"
              className="mt-2 flex items-center gap-4 text-[11px] text-neutral-600 px-1"
            >
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full border" style={{ background: "#f59e0b33", borderColor: "#f59e0b" }} />
                Active this month
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full border" style={{ background: "#1A433110", borderColor: "#1A4331" }} />
                Quiet
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span className="inline-block w-3 h-3 rounded-full border" style={{ background: "#D3613520", borderColor: "#D36135" }} />
                Yours
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
